import { queryOptions } from "@tanstack/react-query"
import * as InFlowEnums from "ipctg-inflow-types/InFlowEnums"

import api from "@/utilities/api"

import { resolveApiMode } from "@/utilities/config"
import {
  ParkCheckoutError,
  ParkPassesError,
  ParkPayError,
  ParkPaymentIntentElementsError,
  ParkRatesError,
  ParkSessionsError,
  ParkValidPassesError,
} from "@/utilities/expectedErrors"

import {
  CHECKOUT_BASE_KEY,
  CONFIRM_PAYMENT_BASE_KEY,
  ME_BASE_KEY,
  PARK_BASE_KEY,
  PASSES_BASE_KEY,
  PAYMENT_INTENT_BASE_KEY,
  PAYMENT_PROCESSOR_BASE_KEY,
  RATES_BASE_KEY,
  RECEIPT_BASE_KEY,
  SESSIONS_BASE_KEY,
  VALID_PASSES_BASE_KEY,
} from "./keys"

export const parkSessionsQueryOptions = () => {
  return queryOptions({
    queryKey: [ME_BASE_KEY, SESSIONS_BASE_KEY],
    queryFn: async () => {
      const cognitoIDToken = await api.auth.getCognitoIDToken()
      if (resolveApiMode() !== "park" || !cognitoIDToken)
        return { parkingSessions: [] }

      const rsp = await api.park.sessions.POST({ cognitoIDToken })

      if ("failReason" in rsp) {
        throw new ParkSessionsError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

export const parkValidPassesQueryOptions = () => {
  return queryOptions({
    queryKey: [ME_BASE_KEY, PASSES_BASE_KEY, VALID_PASSES_BASE_KEY],
    queryFn: async () => {
      const cognitoIDToken = await api.auth.getCognitoIDToken()
      if (resolveApiMode() !== "park" || !cognitoIDToken) return { passes: [] }

      const rsp = await api.park.validPasses.POST({ cognitoIDToken })

      if ("failReason" in rsp) {
        throw new ParkValidPassesError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

export const parkRatesQueryOptions = () => {
  return queryOptions({
    queryKey: [PARK_BASE_KEY, RATES_BASE_KEY],
    queryFn: async () => {
      const rsp = await api.park.rates.POST({})
      if ("failReason" in rsp) {
        throw new ParkRatesError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
    gcTime: 0,
    refetchInterval: 60000, // Refetch every minute (60,000ms)
  })
}

export const parkCheckoutQueryOptions = (args: {
  rateID: string
  epoch: string
}) => {
  return queryOptions({
    queryKey: [PARK_BASE_KEY, CHECKOUT_BASE_KEY, args.rateID, args.epoch],
    queryFn: async () => {
      const rsp = await api.park.checkout.POST({
        rateID: args.rateID,
        isPurchasingPass: false,
      })

      if ("failReason" in rsp) {
        throw new ParkCheckoutError(rsp.failReason)
      }

      // 1 second delay after checkout
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return rsp
    },
    retry: false,
    gcTime: 0, // Never cache - always fetch fresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const parkPaymentIntentElementsQueryOptions = (args: {
  email: string | null
  phone: string | null
  contactType: InFlowEnums.ContactType
  epoch: string
  rateID: string
}) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      PARK_BASE_KEY,
      PAYMENT_INTENT_BASE_KEY,
      args,
    ],
    queryFn: async () => {
      const cognitoIDToken = await api.auth.getCognitoIDToken()

      const rsp = await api.park.paymentIntentElements.POST({
        cognitoIDToken,
        email: args.email,
        phone: args.phone,
        contactType: args.contactType,
      })

      if ("failReason" in rsp) {
        if (
          rsp.failReason === InFlowEnums.CheckoutFailReason.CognitoTokenExpired
        ) {
          await api.auth.signOut()
        }
        throw new ParkPaymentIntentElementsError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const confirmParkPaymentQueryOptions = () => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      PARK_BASE_KEY,
      CONFIRM_PAYMENT_BASE_KEY,
    ],
    queryFn: async () => {
      const rsp = await api.park.pay.POST({})

      if ("failReason" in rsp) {
        throw new ParkPayError(rsp.failReason)
      }

      return rsp
    },
    retry: (failureCount, error) => {
      // Retry up to 8 times for specific errors
      if (failureCount >= 8) return false

      // Only retry for PaymentWebhookNotCompleted and SystemError
      if (error instanceof ParkPayError) {
        return ["PaymentWebhookNotCompleted", "SystemError"].includes(
          error.failReason
        )
      }

      // Retry network errors
      return true
    },
    // First retry has no delay (skipped by TanStack Query), subsequent retries wait 1 second
    retryDelay: 1000,
  })
}

export const parkPassesQueryOptions = () => {
  return queryOptions({
    queryKey: [ME_BASE_KEY, PASSES_BASE_KEY],
    queryFn: async () => {
      const cognitoIDToken = await api.auth.getCognitoIDToken()
      if (!cognitoIDToken) return { passes: [] }

      const rsp = await api.park.passes.POST({ cognitoIDToken })
      if ("failReason" in rsp) {
        throw new ParkPassesError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

/**
 * Fetch receipt details
 */
export const parkReceiptQueryOptions = (args: { receiptID: string }) => {
  return queryOptions({
    queryKey: [PARK_BASE_KEY, RECEIPT_BASE_KEY, args],
    queryFn: async () => api.receipt.POST({ receiptID: args.receiptID }),
    retry: false,
  })
}
